import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { toast } from "react-toastify";
import usePageMeta from "../../hooks/usePageMeta";
import {
    getAllStoreUsersAction, createStoreUserAction, updateStoreUserAction, deleteStoreUserAction,
    getAllStoresAction, clearAdminErrors,
} from "../../redux/actions/adminActions";
import { ADMIN_USER_MUTATE_RESET } from "../../redux/constants/adminConstants";

const ALL_MENUS = [
    { key: "scan", label: "Scan / Dashboard" },
    { key: "analytics", label: "Analytics & Forecasting" },
    { key: "upc_data", label: "Scanned UPC Data / Inventory Lookup" },
    { key: "purchase_records", label: "Purchase Records" },
    { key: "sale_records", label: "Sale Records" },
    { key: "export_excel", label: "Export Excel" },
    { key: "products", label: "Products" },
];

const EMPTY_FORM = { name: "", email: "", password: "", store_id: "", permissions: [] };

const UserManagement = () => {
    const dispatch = useDispatch();
    const { users, loading } = useSelector(s => s.adminUsers);
    const { loading: mutating, success, message, error } = useSelector(s => s.adminUserMutate);
    const { stores } = useSelector(s => s.adminStores);

    const [storeFilter, setStoreFilter] = useState("");
    const [modal, setModal] = useState(null);
    const [form, setForm] = useState(EMPTY_FORM);
    const [deleteTarget, setDeleteTarget] = useState(null);

    usePageMeta("userManagement");

    useEffect(() => {
        dispatch(getAllStoresAction());
        dispatch(getAllStoreUsersAction());
    }, [dispatch]);

    useEffect(() => {
        if (success) {
            toast.success(message);
            dispatch({ type: ADMIN_USER_MUTATE_RESET });
            setModal(null);
            setDeleteTarget(null);
            dispatch(getAllStoreUsersAction(storeFilter || undefined));
        }
        if (error) {
            toast.error(error);
            dispatch(clearAdminErrors());
        }
    }, [success, message, error, dispatch, storeFilter]);

    const handleFilterChange = (val) => {
        setStoreFilter(val);
        dispatch(getAllStoreUsersAction(val || undefined));
    };

    const togglePermission = (key) => {
        setForm(f => ({
            ...f,
            permissions: f.permissions.includes(key)
                ? f.permissions.filter(k => k !== key)
                : [...f.permissions, key]
        }));
    };

    const openCreate = () => { setForm(EMPTY_FORM); setModal({ mode: "create" }); };
    const openEdit = (u) => {
        setForm({ name: u.name, email: u.email, password: "", store_id: String(u.store_id), permissions: u.permissions || [] });
        setModal({ mode: "edit", user: u });
    };

    const handleSubmit = (e) => {
        e.preventDefault();
        if (!form.name || !form.email || !form.store_id) { toast.error("Name, email, and store are required"); return; }
        if (modal.mode === "create" && !form.password) { toast.error("Password is required for new users"); return; }

        const payload = { ...form, store_id: Number(form.store_id) };
        if (!payload.password) delete payload.password;

        if (modal.mode === "create") dispatch(createStoreUserAction(payload));
        else dispatch(updateStoreUserAction(modal.user.id, payload));
    };

    return (
        <div className="pl-[1.5vmax] pr-[2.5vmax] mt-[1vmax] mb-[4vmax]">
            <div className="flex justify-between items-center bg-white p-[1.2vmax] rounded-[1vmax] px-[2vmax] mb-[2vmax] flex-wrap gap-[1vmax]">
                <h2 className="text-[1.3vmax] font-bold">User Management</h2>
                <div className="flex items-center gap-[1vmax]">
                    <select
                        className="border border-slate-300 rounded-lg px-[1vmax] py-[0.5vmax] text-[1vmax] outline-[var(--color-2)]"
                        value={storeFilter}
                        onChange={e => handleFilterChange(e.target.value)}
                    >
                        <option value="">All Stores</option>
                        {stores.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
                    </select>
                    <button
                        onClick={openCreate}
                        className="bg-[var(--color-2)] hover:opacity-90 text-white text-[1vmax] py-[0.6vmax] px-[1.5vmax] rounded-[0.5vmax] flex items-center gap-[0.5vmax]"
                    >
                        <i className="fa-solid fa-plus" /> Add User
                    </button>
                </div>
            </div>

            <div className="bg-white shadow-1 rounded-2xl overflow-hidden">
                {loading ? (
                    <p className="text-center py-[4vmax] text-slate-400">Loading...</p>
                ) : users.length === 0 ? (
                    <p className="text-center py-[4vmax] text-slate-400">No users found.</p>
                ) : (
                    <table className="w-full text-[1vmax]">
                        <thead className="bg-slate-50 border-b">
                            <tr>
                                {[
                                    { h: "#", align: "th-center" },
                                    { h: "Name", align: "th-text" },
                                    { h: "Email", align: "th-text" },
                                    { h: "Store", align: "th-text" },
                                    { h: "Permissions", align: "th-text" },
                                    { h: "Actions", align: "th-center" },
                                ].map(({ h, align }) => (
                                    <th key={h} className={`${align} px-[1.5vmax] py-[1vmax] text-slate-600 font-semibold`}>{h}</th>
                                ))}
                            </tr>
                        </thead>
                        <tbody>
                            {users.map((u, i) => (
                                <tr key={u.id} className="border-b hover:bg-slate-50 transition-colors">
                                    <td className="px-[1.5vmax] py-[1vmax] td-center text-slate-400">{i + 1}</td>
                                    <td className="px-[1.5vmax] py-[1vmax] td-text font-semibold">{u.name}</td>
                                    <td className="px-[1.5vmax] py-[1vmax] td-text text-slate-500">{u.email}</td>
                                    <td className="px-[1.5vmax] py-[1vmax] td-text">
                                        <span className="bg-blue-50 text-blue-700 px-[0.8vmax] py-[0.2vmax] rounded-full text-[0.85vmax]">
                                            {u.store_name || "—"}
                                        </span>
                                    </td>
                                    <td className="px-[1.5vmax] py-[1vmax] td-text">
                                        <div className="flex flex-wrap gap-[0.3vmax]">
                                            {(u.permissions || []).length === 0
                                                ? <span className="text-slate-400 text-[0.85vmax]">None</span>
                                                : (u.permissions || []).map(p => (
                                                    <span key={p} className="bg-green-50 text-green-700 text-[0.75vmax] px-[0.5vmax] py-[0.1vmax] rounded">
                                                        {ALL_MENUS.find(m => m.key === p)?.label || p}
                                                    </span>
                                                ))
                                            }
                                        </div>
                                    </td>
                                    <td className="px-[1.5vmax] py-[1vmax] td-center">
                                        <div className="flex gap-[0.8vmax] justify-center">
                                            <button onClick={() => openEdit(u)} className="text-[var(--color-2)] hover:opacity-70 text-[1.1vmax]"><i className="fa-solid fa-pen" /></button>
                                            <button onClick={() => setDeleteTarget(u)} className="text-red-500 hover:opacity-70 text-[1.1vmax]"><i className="fa-solid fa-trash" /></button>
                                        </div>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                )}
            </div>

            {/* Modal */}
            {modal && (
                <div className="fixed inset-0 bg-black/50 z-[999] flex-center">
                    <div className="bg-white rounded-2xl p-[2vmax] w-[90%] lg:w-[40%] shadow-xl max-h-[90vh] overflow-y-auto">
                        <h3 className="font-bold text-[1.3vmax] mb-[1.5vmax]">
                            {modal.mode === "create" ? "Create Store User" : "Edit Store User"}
                        </h3>
                        <form onSubmit={handleSubmit} className="flex flex-col gap-[1vmax]">
                            <div className="grid grid-cols-2 gap-[1vmax]">
                                <div className="flex flex-col gap-[0.4vmax]">
                                    <label className="text-[1vmax] font-semibold text-slate-600">Name <span className="text-red-500">*</span></label>
                                    <input className="border border-slate-300 rounded-xl p-[0.8vmax] text-[1vmax] outline-[var(--color-2)]"
                                        value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))} placeholder="Full name" />
                                </div>
                                <div className="flex flex-col gap-[0.4vmax]">
                                    <label className="text-[1vmax] font-semibold text-slate-600">Email <span className="text-red-500">*</span></label>
                                    <input type="email" className="border border-slate-300 rounded-xl p-[0.8vmax] text-[1vmax] outline-[var(--color-2)]"
                                        value={form.email} onChange={e => setForm(f => ({ ...f, email: e.target.value }))} placeholder="user@store.com" />
                                </div>
                                <div className="flex flex-col gap-[0.4vmax]">
                                    <label className="text-[1vmax] font-semibold text-slate-600">
                                        Password {modal.mode === "edit" && <span className="text-slate-400 font-normal">(leave blank to keep)</span>}
                                        {modal.mode === "create" && <span className="text-red-500"> *</span>}
                                    </label>
                                    <input type="password" className="border border-slate-300 rounded-xl p-[0.8vmax] text-[1vmax] outline-[var(--color-2)]"
                                        value={form.password} onChange={e => setForm(f => ({ ...f, password: e.target.value }))} placeholder="Password" />
                                </div>
                                <div className="flex flex-col gap-[0.4vmax]">
                                    <label className="text-[1vmax] font-semibold text-slate-600">Store <span className="text-red-500">*</span></label>
                                    <select className="border border-slate-300 rounded-xl p-[0.8vmax] text-[1vmax] outline-[var(--color-2)]"
                                        value={form.store_id} onChange={e => setForm(f => ({ ...f, store_id: e.target.value }))}>
                                        <option value="">Select a store</option>
                                        {stores.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
                                    </select>
                                </div>
                            </div>

                            <div className="flex flex-col gap-[0.5vmax]">
                                <label className="text-[1vmax] font-semibold text-slate-600">Menu Permissions</label>
                                <div className="grid grid-cols-2 gap-[0.5vmax]">
                                    {ALL_MENUS.map(({ key, label }) => (
                                        <label key={key} className="flex items-center gap-[0.5vmax] text-[1vmax] cursor-pointer p-[0.5vmax] rounded-lg hover:bg-slate-50">
                                            <input
                                                type="checkbox"
                                                checked={form.permissions.includes(key)}
                                                onChange={() => togglePermission(key)}
                                                className="accent-[var(--color-2)]"
                                            />
                                            {label}
                                        </label>
                                    ))}
                                </div>
                                <p className="text-[0.85vmax] text-slate-400">Users with no permissions assigned will see a blank panel.</p>
                            </div>

                            <div className="flex justify-end gap-[1vmax]">
                                <button type="button" onClick={() => setModal(null)} className="px-[1.5vmax] py-[0.6vmax] rounded-lg bg-slate-100 text-[1vmax]">Cancel</button>
                                <button type="submit" disabled={mutating} className="px-[1.5vmax] py-[0.6vmax] rounded-lg bg-[var(--color-2)] text-white text-[1vmax] disabled:opacity-60">
                                    {mutating ? "Saving..." : "Save"}
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}

            {/* Delete Confirm */}
            {deleteTarget && (
                <div className="fixed inset-0 bg-black/50 z-[999] flex-center">
                    <div className="bg-white rounded-2xl p-[2vmax] w-[90%] lg:w-[30%] shadow-xl text-center">
                        <i className="fa-solid fa-triangle-exclamation text-red-500 text-[3vmax] mb-[1vmax]" />
                        <h3 className="font-bold text-[1.2vmax] mb-[0.5vmax]">Delete User</h3>
                        <p className="text-slate-500 text-[1vmax] mb-[1.5vmax]">Delete <strong>{deleteTarget.name}</strong>? This cannot be undone.</p>
                        <div className="flex justify-center gap-[1vmax]">
                            <button onClick={() => setDeleteTarget(null)} className="px-[1.5vmax] py-[0.6vmax] rounded-lg bg-slate-100 text-[1vmax]">Cancel</button>
                            <button onClick={() => dispatch(deleteStoreUserAction(deleteTarget.id))} disabled={mutating} className="px-[1.5vmax] py-[0.6vmax] rounded-lg bg-red-500 text-white text-[1vmax] disabled:opacity-60">
                                {mutating ? "Deleting..." : "Delete"}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default UserManagement;
